import { useMemo } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Check, Loader2, AlertTriangle } from "lucide-react";
import plageBackground from "@/assets/img/plage-background.png";
import nautikaIcon from "@/assets/img/nautika.svg";
import { api } from "@/lib/api";
import { authClient } from "@/lib/auth-client";
import type { EnrichedSubscriptionBase } from "nautika-types";

export function SubscriptionConfirmationPage() {
  // Polling is handled via refetchInterval; no explicit retry counter needed
  const navigate = useNavigate();

  // Utiliser api.useQuery pour récupérer les abonnements avec une stratégie de retry
  const {
    data: subscriptions,
    isLoading: loading,
    error: queryError,
  } = api.useQuery(
    "get",
    "/subscriptions/list",
    {},
    {
      staleTime: 0,
      refetchInterval: 2000,
      refetchIntervalInBackground: false,
    }
  );

  const foundActiveSubscription = useMemo(() => {
    if (!Array.isArray(subscriptions)) return null;
    return (subscriptions.find((sub) => sub.status === "active" || sub.status === "trialing") || null) as EnrichedSubscriptionBase | null;
  }, [subscriptions]);

  const pollingExceeded = useMemo(() => {
    // Approximate based on query-level polling for ~20s
    // Without explicit timers, we show a softer message if nothing is found.
    return !loading && Array.isArray(subscriptions) && subscriptions.length === 0;
  }, [loading, subscriptions]);
  const formatDate = (date: Date | string | number | Record<string, never> | null | undefined) => {
    if (!date) return null;
    if (date instanceof Date) {
      return date.toLocaleDateString("fr-FR");
    } else if (typeof date === "string") {
      return new Date(date).toLocaleDateString("fr-FR");
    } else if (typeof date === "number") {
      return new Date(date * 1000).toLocaleDateString("fr-FR");
    }
    return null;
  };

  const getOrderId = (subscription: EnrichedSubscriptionBase) => {
    return subscription.stripeData?.latestInvoiceNumber || subscription.stripeData?.latestInvoiceId || subscription.id;
  };

  const getAmount = (subscription: EnrichedSubscriptionBase) => {
    if (subscription.stripeData?.amount) {
      return (subscription.stripeData.amount / 100).toFixed(0);
    }
    return null;
  };

  const getCurrency = (subscription: EnrichedSubscriptionBase) => {
    return subscription.stripeData?.currency === "eur" ? "€" : subscription.stripeData?.currency?.toUpperCase() || "€";
  };

  const getCreationDate = (subscription: EnrichedSubscriptionBase) => {
    return formatDate(subscription.stripeData?.latestInvoiceDate || subscription.periodStart);
  };

  const getNextBillingDate = (subscription: EnrichedSubscriptionBase) => {
    return formatDate(subscription.stripeData?.currentPeriodEnd || subscription.periodEnd);
  };

  const getTrialEndDate = (subscription: EnrichedSubscriptionBase) => {
    return formatDate(subscription.stripeData?.trialEnd);
  };
  const handleGoToDashboard = () => {
    navigate("/dashboard");
  };

  const handleGoToOnboarding = async () => {
    await authClient.updateUser({
      onboarding: "step8",
    });
    navigate("/onboarding/step8");
  };

  return (
    <div className="relative min-h-svh overflow-hidden">
      {/* Background image covering the entire viewport */}
      <div className="absolute inset-0 min-h-svh min-w-svw">
        <img src={plageBackground} alt="Background" className="h-full w-full object-cover" />
      </div>

      {/* Semi-transparent overlay for better contrast */}
      <div className="absolute inset-0 bg-white/20"></div>

      {/* Content */}
      <div className="relative z-10 flex min-h-svh flex-col items-center justify-center px-4 py-12">
        <div className="mb-8 flex items-center gap-4 text-2xl font-bold">
          <img src={nautikaIcon} alt="Nautika" className="h-12 w-12" />
          Nautika
        </div>{" "}
        <Card className="w-full max-w-md bg-white px-2">
          {loading || (!foundActiveSubscription && !pollingExceeded) ? (
            <div className="flex flex-col items-center justify-center p-8">
              <Loader2 className="mb-4 h-8 w-8 animate-spin text-blue-500" />
              <p className="text-muted-foreground">Finalisation de votre abonnement en cours...</p>
            </div>
          ) : !foundActiveSubscription ? (
            <>
              <CardHeader className="flex flex-col items-center justify-center text-center">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <CardTitle className="text-2xl font-bold text-red-600">Problème d'abonnement</CardTitle>
                <CardDescription>Aucun abonnement actif n'a été trouvé pour votre compte.</CardDescription>
              </CardHeader>

              <CardContent>
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <p className="mb-4 text-center text-sm text-red-600">
                    Il semble qu'il y ait eu un problème avec votre abonnement ou que celui-ci ne se soit pas correctement finalisé.
                  </p>
                  <p className="text-center text-sm text-red-600">Veuillez recommencer le processus d'abonnement pour résoudre ce problème.</p>
                </div>
              </CardContent>

              <CardFooter className="flex justify-center">
                <Button onClick={handleGoToOnboarding} className="w-full bg-blue-500 hover:bg-blue-600">
                  Recommencer l'abonnement
                </Button>
              </CardFooter>
            </>
          ) : foundActiveSubscription ? (
            <>
              <CardHeader className="flex flex-col items-center justify-center text-center">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                  <Check className="h-6 w-6 text-green-600" />
                </div>
                <CardTitle className="text-2xl font-bold">
                  Merci pour votre {foundActiveSubscription.status === "trialing" ? "essai" : "abonnement"} !
                </CardTitle>
                <CardDescription>
                  Votre {foundActiveSubscription.status === "trialing" ? "essai gratuit" : "abonnement"} a été activé avec succès.
                </CardDescription>
              </CardHeader>

              <CardContent>
                <div className="rounded-lg border p-4">
                  <h3 className="mb-4 text-lg font-semibold">Récapitulatif de la commande</h3>

                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Numéro de commande</span>
                      <span className="font-medium">{getOrderId(foundActiveSubscription)}</span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Date</span>
                      <span className="font-medium">{getCreationDate(foundActiveSubscription)}</span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Montant facturé</span>
                      <span className="font-medium">
                        {getTrialEndDate(foundActiveSubscription) ? "0" : getAmount(foundActiveSubscription)} {getCurrency(foundActiveSubscription)}
                      </span>
                    </div>

                    {foundActiveSubscription.status !== "trialing" && getNextBillingDate(foundActiveSubscription) && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Prochain prélèvement</span>
                        <span className="font-medium">{getNextBillingDate(foundActiveSubscription)}</span>
                      </div>
                    )}

                    {foundActiveSubscription.status === "trialing" ? (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Fin de l'essai</span>
                        <span className="font-medium">{getTrialEndDate(foundActiveSubscription)}</span>
                      </div>
                    ) : null}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Statut</span>
                      <span className="font-medium">{foundActiveSubscription.status === "trialing" ? "Essai gratuit" : "Abonnement"}</span>
                    </div>
                  </div>

                  <Separator className="my-4" />

                  <div className="flex items-center gap-2 text-green-600">
                    <Check className="h-4 w-4" />
                    <span className="font-medium">Votre {foundActiveSubscription.status === "trialing" ? "essai gratuit" : "abonnement"} est actif</span>
                  </div>

                  <p className="mt-4 text-sm text-muted-foreground">Un email de confirmation avec la facture a été envoyé à votre adresse mail</p>
                </div>
              </CardContent>

              <CardFooter className="flex justify-center">
                <Button onClick={handleGoToDashboard} className="w-full bg-blue-500 hover:bg-blue-600">
                  Accéder à mon espace
                </Button>
              </CardFooter>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center p-8">
              <Loader2 className="mb-4 h-8 w-8 animate-spin text-blue-500" />
              <p className="text-muted-foreground">Recherche de vos données d'abonnement...</p>
            </div>
          )}
        </Card>
        {queryError && <div className="mt-4 text-center text-red-500">Erreur lors de la récupération des détails de l'abonnement</div>}
      </div>
    </div>
  );
}
